import { AnyAction, createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActionsObservable, combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { api } from '~constants/api';
import { extractPaginationValues, getPaginationQueryParams } from '~features/page/pageUtils';
import { http } from '~services/index';

import { actions as notificationsActions } from '../notifications/notificationsSlice';
import { actions as pageActions } from '../page/pageSlice';
import { actions as sharedActions } from '../sharedSlice';

import { AssetsImage, AssociateImages, PaginationRequestPayload, PaginationResponse, ResponseError } from '~models';
import { getResponseError, intToHex } from '~utils';

interface AssetsState {
  data: {
    images: {
      count: number;
      list: AssetsImage[] | null;
      image: AssetsImage | null;
    },
    associatedImages: AssociateImages | null;
  },
  loading: {
    list: boolean;
    image: boolean;
    upload: boolean;
    delete: boolean;
    update: boolean;
    associatedImages: boolean;
  },
  status: 'updated' | 'uploaded' | 'deleted' | null;
  error: ResponseError | null;
}

const initialState: AssetsState = {
  data: {
    images: {
      count: 0,
      list: null,
      image: null,
    },
    associatedImages: null,
  },
  loading: {
    list: false,
    image: false,
    upload: false,
    delete: false,
    update: false,
    associatedImages: false,
  },
  status: null,
  error: null,
};

export const { name, actions, reducer } = createSlice({
  name: 'assets',
  initialState,
  reducers: {

    fetchAssetsImagesCountInit: state => {
      state.loading.image = true;
    },

    fetchAssetsImagesCountSuccess: (state, { payload: { count } }: PayloadAction<{ count: number }>) => {
      state.loading.image = false;
      state.data.images.count = count;
    },

    fetchAssetsImagesFailed: state => {
      state.loading.image = false;
    },

    fetchAssetsImagesListInit:
    (state, payload: PayloadAction<PaginationRequestPayload & { name?: string, allList?: boolean }>) => {
      state.loading.list = true;
      state.data.images.list = null;
    },

    fetchAssetsImagesListSuccess: (state, { payload: { content } }: PayloadAction<{ content: AssetsImage[] }>) => {
      state.loading.list = false;
      state.data.images.list = content;
    },

    fetchAssetsImagesListFailed: state => {
      state.loading.list = false;
    },

    fetchAssetsImageByIdInit: (state, payload: PayloadAction<{ id: number }>) => {
      state.loading.image = true;
    },

    fetchAssetsImageByIdSuccess: (state, { payload }: PayloadAction<AssetsImage>) => {
      state.loading.image = false;
      state.data.images.image = payload;
    },

    uploadAssetsImageInit: (state, { payload }: PayloadAction<Partial<AssetsImage>>) => {
      state.loading.upload = true;
      state.status = null;
      state.error = null;
    },

    uploadAssetsImageSuccess: state => {
      state.loading.upload = false;
      state.status = 'uploaded';
    },

    uploadAssetsImageFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.upload = false;
      state.error = payload;
    },

    updateAssetsImageInit: (state,
      { payload: { id, values } }: PayloadAction<{ id: number, values: Partial<AssetsImage> }>) => {
      state.loading.update = true;
      state.status = null;
      state.error = null;
    },

    updateAssetsImageSuccess: state => {
      state.loading.update = false;
      state.status = 'updated';
    },

    updateAssetsImageFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.update = false;
      state.error = payload;
    },

    deleteAssetsImageInit: (state, action: PayloadAction<{ id: number }>) => {
      state.loading.delete = true;
      state.status = null;
    },

    deleteAssetsImageSuccess: state => {
      state.loading.delete = false;
      state.status = 'deleted';
    },

    deleteAssetsImageFailed: state => {
      state.loading.delete = false;
    },

    fetchAssociatedImagesInit: (state, action: PayloadAction<{ buId: number, unitId: number }>) => {
      state.loading.associatedImages = true;
    },

    fetchAssociatedImagesSuccess: (state, { payload }: PayloadAction<AssociateImages>) => {
      state.loading.associatedImages = false;
      state.data.associatedImages = payload;
    },

    fetchAssociatedImagesFailed: state => {
      state.loading.associatedImages = false;
    },

    updateAssociatedImagesInit: (state,
      action: PayloadAction<{ buId: number, unitId: number, values: { imageIds: number[]}}>) => {
      state.loading.associatedImages = true;
    },

    updateAssociatedImagesSuccess: state => {
      state.loading.associatedImages = false;
    },

    reset(state) {
      Object.assign(state, initialState);
    },

    resetAssetsStatus(state) {
      state.status = null;
    },
  },

  extraReducers: {
    [sharedActions.reset.toString()]: state => Object.assign(state, initialState),
  },
});

const getAssetsState = (state: AES.RootState) => state.assets;

export const selectors = {
  getAssetsLoaders: createDraftSafeSelector(getAssetsState, state => state.loading),
  getAssetsError: createDraftSafeSelector(getAssetsState, state => state.error),
  isUpdated: createDraftSafeSelector(getAssetsState, state => state.status === 'updated'),
  isImageListChanged: createDraftSafeSelector(getAssetsState, state =>
    state.status === 'uploaded' || state.status === 'deleted' || state.status === 'updated'),
  getImagesCount: createDraftSafeSelector(getAssetsState, state => state.data.images.count),
  getImagesList: createDraftSafeSelector(getAssetsState, state => state.data.images.list),
  getImage: createDraftSafeSelector(getAssetsState, state => state.data.images.image),
  getAssociatedImages: (unitId: number) => createDraftSafeSelector(getAssetsState, state =>
    (unitId === state.data.associatedImages?.unitId ? state.data.associatedImages : null)
  ),
};

const fetchAssetImagesCountEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAssetsImagesCountInit.match),
    mergeMap(() =>
      http.getJSON<number>(api.assets.images.count).pipe(
        mergeMap(count => of(actions.fetchAssetsImagesCountSuccess({ count }))),
        catchError(() => of(actions.fetchAssetsImagesFailed()),
        )
      )
    )
  );

export const fetchAssetImagesListEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(action => actions.fetchAssetsImagesListInit.match(action)),
    mergeMap(({ payload }) => {
      let searchParams = '';
      if (!payload?.allList) {
        searchParams = (payload?.name && payload.name.length > 2) ?
          `${getPaginationQueryParams(payload)}&name=${payload.name}` :
          getPaginationQueryParams(payload);
      }

      return http.getJSON<PaginationResponse<AssetsImage>>(api.assets.images.list(searchParams)).pipe(
        mergeMap(res => of(
          actions.fetchAssetsImagesListSuccess(res),
          pageActions.setPagePagination(extractPaginationValues(res)))
        ),
        catchError(() => of(actions.fetchAssetsImagesListFailed()),
        )
      );
    })
  );

export const fetchAssetsImageByIdEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAssetsImageByIdInit.match),
    mergeMap(({ payload }) =>
      http.getJSON<AssetsImage>(api.assets.images.byId(payload.id)).pipe(
        mergeMap(res => of(actions.fetchAssetsImageByIdSuccess(res))),
        catchError(() => of(actions.fetchAssetsImagesFailed()))
      ))
  );

export const uploadAssetsImageEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.uploadAssetsImageInit.match),
    mergeMap(({ payload }) =>
      http.post(api.assets.images.upload, payload).pipe(
        mergeMap(() => of(
          actions.uploadAssetsImageSuccess(),
          notificationsActions.enqueue({
            message: 'Image uploaded',
            options: { variant: 'success' },
          })
        )),
        catchError(err => of(actions.uploadAssetsImageFailed(getResponseError(err))))
      )
    )
  );

export const updateAssetsImageEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateAssetsImageInit.match),
    mergeMap(({ payload: { id, values } }) =>
      http.put(api.assets.images.byId(id), values).pipe(
        mergeMap(() =>
          of(
            actions.updateAssetsImageSuccess(),
            notificationsActions.enqueue({
              message: 'Image updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => of(actions.updateAssetsImageFailed(getResponseError(err)))
        )
      ))
  );

export const deleteAssetsImageEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.deleteAssetsImageInit.match),
    mergeMap(({ payload }) =>
      http.delete(api.assets.images.byId(payload.id)).pipe(
        mergeMap(() => of(
          actions.deleteAssetsImageSuccess(),
          notificationsActions.enqueue({
            message: 'Image deleted',
            options: { variant: 'success' },
          })
        )),
        catchError(() => of(actions.deleteAssetsImageFailed()))
      ))
  );

export const fetchAssociatedImagesEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchAssociatedImagesInit.match),
    mergeMap(({ payload }) =>
      http.getJSON<AssociateImages>(api.assets.associatedImages(payload.buId, payload.unitId)).pipe(
        mergeMap(res => of(actions.fetchAssociatedImagesSuccess(res))),
        catchError(() => of(actions.fetchAssociatedImagesFailed()))
      ))
  );

export const updateAssociatedImagesEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateAssociatedImagesInit.match),
    mergeMap(({ payload }) =>
      http.post(api.assets.associatedImages(payload.buId, payload.unitId), payload.values).pipe(
        mergeMap(() => of(
          actions.updateAssociatedImagesSuccess(),
          notificationsActions.enqueue({
            message: `Unit: ${intToHex(payload.unitId)} Image list updated`,
            options: { variant: 'success' },
          })
        )),
        catchError(() => of(actions.fetchAssociatedImagesFailed()))
      )
    )
  );

export const epics = combineEpics(
  fetchAssetImagesCountEpic,
  fetchAssetImagesListEpic,
  fetchAssetsImageByIdEpic,
  uploadAssetsImageEpic,
  updateAssetsImageEpic,
  deleteAssetsImageEpic,
  fetchAssociatedImagesEpic,
  updateAssociatedImagesEpic,
);

export const allEpics = {
  fetchAssetImagesCountEpic,
  fetchAssetImagesListEpic,
  fetchAssetsImageByIdEpic,
  uploadAssetsImageEpic,
  updateAssetsImageEpic,
  deleteAssetsImageEpic,
  fetchAssociatedImagesEpic,
  updateAssociatedImagesEpic,
};

declare global {
  namespace AES {
    export interface RootState {
      assets: AssetsState;
    }
    export interface Actions {
      assets: typeof actions;
    }

    export interface Selectors {
      assets: typeof selectors;
    }
  }
}

